import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './TargetInvoiceDetailsModal.module.css';
import { targetAnalyticsApi } from '../../../../../api/targetAnalytics/targetAnalyticsApi';
import TargetInvoiceTableModal from './TargetInvoiceTableModal';

const InvoiceFilterModal = ({
  show,
  onClose,
  preSelectedField = null,
  selectedFilterType = 'region',
  selectedYear = 'FY 2025-26'
}) => {
  const [currentYear, setCurrentYear] = useState(selectedYear);
  const [currentFilterType, setCurrentFilterType] = useState(selectedFilterType);
  const [selectedFilterValue, setSelectedFilterValue] = useState('');
  const [filterOptions, setFilterOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const yearOptions = ['FY 2025-26', 'FY 2024-25', 'Complete'];

  const filterTypeOptions = [
    { value: 'region',      label: 'Region'       },
    { value: 'state',       label: 'State'        },
    { value: 'salesperson', label: 'Sales Person' },
    { value: 'category',    label: 'Category'     },
  ];

  const staticFilterOptions = {
    region: ['Central', 'South', 'West 1', 'West 2', 'North', 'East', 'Overseas'],
    state: [
      'Andhra Pradesh', 'Assam', 'Chandigarh',
      'Dadra & Nagar Haveli and Daman & Diu', 'Delhi', 'Goa',
      'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
      'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra',
      'Meghalaya', 'Puducherry', 'Punjab', 'Rajasthan',
      'Tamil Nadu', 'Telangana', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    ],
  };

  // Sync props → state when modal opens
  useEffect(() => {
    if (show) {
      setCurrentYear(selectedYear);
      setCurrentFilterType(selectedFilterType);
      setSelectedFilterValue(preSelectedField || '');
      setShowInvoiceModal(false);
      setInvoiceData([]);
    }
  }, [show, selectedYear, selectedFilterType, preSelectedField]);

  // Load filter options whenever type changes
  useEffect(() => {
    if (currentFilterType === 'region' || currentFilterType === 'state') {
      setFilterOptions(staticFilterOptions[currentFilterType] || []);
    } else {
      fetchFilterOptions();
    }
  }, [currentFilterType]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [show]);

  const fetchFilterOptions = async () => {
    setLoading(true);
    try {
      const result = currentFilterType === 'salesperson'
        ? await targetAnalyticsApi.getSalesPersons()
        : await targetAnalyticsApi.getCategories();
      setFilterOptions(result?.data || []);
    } catch (err) {
      console.error('Error fetching filter options:', err);
      setFilterOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFilterValue) {
      alert('Please select a filter value');
      return;
    }
    setLoadingInvoices(true);
    try {
      const result = await targetAnalyticsApi.getInvoiceDetails(
        currentYear, currentFilterType, selectedFilterValue
      );
      if (result?.data && result.data.length > 0) {
        setInvoiceData(result.data);
        setShowInvoiceModal(true);
      } else {
        alert('No invoices found for the selected filters');
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
      alert('Failed to fetch invoice details');
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleMainModalClose = () => {
    setShowInvoiceModal(false);
    setInvoiceData([]);
    setSelectedFilterValue('');
    onClose();
  };

  const getModalTitle = () => {
    if (selectedFilterValue) {
      const label = filterTypeOptions.find((f) => f.value === currentFilterType)?.label || 'Filter';
      return `Invoice Details - ${label}: ${selectedFilterValue} (${currentYear})`;
    }
    return 'Invoice Details';
  };

  if (!show) return null;

  // ── Rendered via portal so it always sits on top of everything ──────────────
  return ReactDOM.createPortal(
    <>
      {/* Filter selection modal */}
      {!showInvoiceModal && (
        <div className={styles.overlay} onClick={handleMainModalClose}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>🔍 Select Invoice Filters</span>
              <button className={styles.closeBtn} onClick={handleMainModalClose}>✕</button>
            </div>

            {/* Body */}
            <div className={styles.modalBody}>
              {/* Financial Year */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Financial Year</label>
                <select
                  value={currentYear}
                  onChange={(e) => setCurrentYear(e.target.value)}
                  className={styles.formSelect}
                >
                  {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>

              {/* Filter Type */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Filter By</label>
                <select
                  value={currentFilterType}
                  onChange={(e) => { setCurrentFilterType(e.target.value); setSelectedFilterValue(''); }}
                  className={styles.formSelect}
                >
                  {filterTypeOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* Filter Value */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Select {filterTypeOptions.find((f) => f.value === currentFilterType)?.label}
                </label>
                {loading ? (
                  <div className={styles.loadingOptions}>
                    <span className={styles.spinner} /> Loading options...
                  </div>
                ) : (
                  <select
                    value={selectedFilterValue}
                    onChange={(e) => setSelectedFilterValue(e.target.value)}
                    className={styles.formSelect}
                    disabled={filterOptions.length === 0}
                  >
                    <option value="">-- Select --</option>
                    {filterOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                )}
                {filterOptions.length === 0 && !loading && (
                  <p className={styles.noOptions}>No options available for this filter type</p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className={styles.modalFooter}>
              <button className={styles.cancelButton} onClick={handleMainModalClose}>
                Cancel
              </button>
              <button
                className={styles.submitButton}
                onClick={handleSubmit}
                disabled={!selectedFilterValue || loadingInvoices}
              >
                {loadingInvoices
                  ? <><span className={styles.spinner} /> Loading...</>
                  : 'Show Invoices'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice table modal */}
      {showInvoiceModal && invoiceData.length > 0 && (
        <TargetInvoiceTableModal
          invoiceData={invoiceData}
          onClose={() => { setShowInvoiceModal(false); setInvoiceData([]); }}
          title={getModalTitle()}
        />
      )}
    </>,
    document.body
  );
};

export default InvoiceFilterModal;